import { useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { Typography } from "ui/typography";

import { Button } from "ui/button";
import { DialogActions } from "ui/dialog";
import { useAddCreativesWithWriterContext } from "components/modals/add-creatives-with-writer-modal/context/add-creatives-with-writer-context";
import CreativeChecklistItem, {
  TEMPLATE_COLUMN_SX,
} from "components/modals/add-creatives-with-writer-modal/steps/creative-selection-step/creative-checklist-item";
import type {
  AddCreativesWithWriterFormData,
  WriterCreative,
} from "components/modals/add-creatives-with-writer-modal/add-creatives-with-writer-modal.types";

import Icons from "assets";

type CreativeSelectionStepProps = {
  creatives: WriterCreative[];
  isLoading: boolean;
  isError: boolean;
};

export const CreativeSelectionStep = ({
  creatives,
  isLoading,
  isError,
}: CreativeSelectionStepProps) => (
  <div className="flex flex-col gap-4">
    <p className="text-base text-gray-900">Please check the campaign content and proceed.</p>

    <div className="flex flex-col gap-2">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 1.5 }}>
        <Typography variant="body1" component="p" color="text.primary" sx={{ flex: 1 }}>
          Selected item(s)
        </Typography>
        {creatives.length > 0 && (
          <Typography variant="body1" component="p" color="text.primary" sx={TEMPLATE_COLUMN_SX}>
            Creative templates
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Icons.Spinner className="size-4 animate-spin" />
          Loading creatives...
        </div>
      ) : isError ? (
        <p className="text-sm text-red-700">
          Could not load this campaign&apos;s creatives. Please close the dialog and try again.
        </p>
      ) : creatives.length > 0 ? (
        <div className="flex flex-col gap-1 max-h-96 overflow-y-auto custom-scroll p-2.5 -m-2.5">
          {creatives.map((creative) => (
            <CreativeChecklistItem key={creative.id} creative={creative} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No creatives found for this campaign.</p>
      )}
    </div>
  </div>
);

type CreativeSelectionStepActionsProps = {
  isError: boolean;
};

export const CreativeSelectionStepActions = ({ isError }: CreativeSelectionStepActionsProps) => {
  const { watch } = useFormContext<AddCreativesWithWriterFormData>();
  const { next: handleNext, setIsOpen } = useAddCreativesWithWriterContext();
  const handleClose = () => setIsOpen(false);

  const selectedCreativeIds = watch("selectedCreativeIds") ?? [];
  const isContinueDisabled = selectedCreativeIds.length === 0 || isError;

  return (
    <DialogActions
      type="steps"
      secondary={
        <Button size="large" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      }
      primary={
        <Button size="large" disabled={isContinueDisabled} onClick={handleNext}>
          Continue with details
        </Button>
      }
    />
  );
};
